import { createRoot, hydrateRoot, type Root } from "react-dom/client";
import {
  detectPreferredLng,
  isSupportedLng,
  type Lng,
} from "./languageDetection";
import ClientApp from "./ClientApp";
import { syncI18nLanguage } from "./router";

export function shouldHydrateClientPath(pathname: string) {
  return Boolean(localeFromPathname(pathname));
}

function localeFromPathname(pathname: string): Lng | null {
  const [, lng] = pathname.split("/");
  return isSupportedLng(lng) ? lng : null;
}

function normalizeHydrationPath(pathname: string) {
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return path.length > 1 ? path.replace(/\/+$/, "") : "/";
}

function readServerRenderedPathname() {
  const pageContextElement = document.getElementById("vike_pageContext");

  if (!pageContextElement?.textContent) {
    return null;
  }

  try {
    const pageContext = JSON.parse(pageContextElement.textContent);
    const catchAllRoute = pageContext?.routeParams?.["*"];

    return typeof catchAllRoute === "string"
      ? normalizeHydrationPath(catchAllRoute)
      : null;
  } catch {
    return null;
  }
}

export function canHydrateServerMarkup(pathname: string) {
  const serverPathname = readServerRenderedPathname();

  return (
    serverPathname === null ||
    serverPathname === normalizeHydrationPath(pathname)
  );
}

function replaceLocationWithLocale(locale: Lng) {
  window.history.replaceState(
    window.history.state,
    "",
    `/${locale}${window.location.search}${window.location.hash}`
  );
}

async function prepareInitialClientRoute(rootElement: HTMLElement) {
  const routeLocale = localeFromPathname(window.location.pathname);

  if (routeLocale) {
    if (!canHydrateServerMarkup(window.location.pathname)) {
      rootElement.replaceChildren();
    }

    await syncI18nLanguage(routeLocale);
    return;
  }

  if (rootElement.hasChildNodes()) {
    rootElement.replaceChildren();
  }

  const preferred = detectPreferredLng();
  await syncI18nLanguage(preferred);
  replaceLocationWithLocale(preferred);
}

export async function renderClientApp(
  rootElement: HTMLElement
): Promise<Root> {
  await prepareInitialClientRoute(rootElement);

  const shouldHydrate =
    rootElement.hasChildNodes() &&
    shouldHydrateClientPath(window.location.pathname);

  if (shouldHydrate) {
    return hydrateRoot(rootElement, <ClientApp />);
  }

  const root = createRoot(rootElement);
  root.render(<ClientApp />);
  return root;
}
